import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { getDocuments, destroyDocument } from '../../../api/DocumentsAPI';
import AppBreadCrumb from '../../common/AppBreadCrumb';
import AppToaster from '../../AppToaster';
import AppPaginator from '../../AppPaginator';
import AppSearchBox from '../../AppSearchBox';
import PerPageBlock from '../../common/PerPageBlock';
import DocumentsReport from './DocumentsReport';

const DocumentsPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const successMessage = location.state?.message;

    const [documentsList, setDocumentsList] = useState({ data: [] });
    const [viewDataLoading, setViewDataLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(process.env.REACT_APP_PER_PAGE_COUNT || 10);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchTermCompleted, setSearchTermCompleted] = useState('');
    const [saveCompleted, setSaveCompleted] = useState(false);
    const [reportLoading, setReportLoading] = useState(false);

    const [showToaster, setShowToaster] = useState(false);
    const [toaster, setToaster] = useState({});

    useEffect(() => {
        if (successMessage) {
            setShowToaster(true);
            setToaster({ type: 'success', heading: 'Success', message: successMessage });
        }
    }, [successMessage]);

    useEffect(() => {
        document.title = 'Manage Documents';
        const filters = {
            paginate: true,
            page: currentPage,
            per_page: perPage,
            search_term: searchTermCompleted,
        };

        setViewDataLoading(true);
        getDocuments(filters, (response) => {
            setViewDataLoading(false);
            if (response.documents) {
                setDocumentsList(response.documents);
            }
        });
    }, [currentPage, perPage, searchTermCompleted, saveCompleted]);

    const handleDeleteClick = (doc) => {
        Swal.fire({
            title: 'Are you sure?',
            html: `<p>You are about to delete the document: <strong>${doc.title}</strong></p><p>This action cannot be undone.</p>`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Yes, delete it!'
        }).then((result) => {
            if (result.isConfirmed) {
                destroyDocument(doc, (response) => {
                    if (!response.error) {
                        Swal.fire('Deleted!', 'The document has been deleted.', 'success');
                        setSaveCompleted(prev => !prev);
                    } else {
                         Swal.fire('Error!', 'Could not delete the document.', 'error');
                    }
                });
            }
        });
    };

    return (
        <>
            <AppToaster showToaster={showToaster} setShowToaster={setShowToaster} toaster={toaster} />
            <main>
                <div className="container-fluid">
                    <AppBreadCrumb title="Documents List">
                          <li><a className="f-s-14 f-w-500" href="#">Docs Master</a></li>
                         <li className="active"><a className="f-s-14 f-w-500" href="#">Documents</a></li>
                    </AppBreadCrumb>

                    <div className="row">
                        <div className="col-12">
                             <div className="tab-wrapper mb-3">
                                <ul className="tabs">
                                    <li className="tab-link active">All Documents</li>
                                    <li className="ms-md-auto">
                                        <div className="text-md-end d-flex flex-column flex-md-row">
                                            <AppSearchBox searchTerm={searchTerm} setSearchTerm={setSearchTerm} setSearchTermCompleted={setSearchTermCompleted} />
                                            <button className="text-nowrap btn btn-primary btn-sm m-md-3 fw-semibold f-s-14" type="button" onClick={() => navigate('/docs/documents/create')}>
                                                <i className="ti ti-plus f-s-18"></i> Add Document
                                            </button>
                                            <DocumentsReport reportLoading={reportLoading} setReportLoading={setReportLoading} />
                                        </div>
                                    </li>
                                </ul>
                            </div>

                            <div className="d-flex m-md-3">
                                {documentsList.data.length > 0 && (
                                    <>
                                        <AppPaginator entityName="Documents" currentPage={documentsList.current_page} totalPages={documentsList.last_page} setCurrentPage={setCurrentPage} />
                                        <PerPageBlock perPage={perPage} setPerPage={setPerPage} />
                                    </>
                                )}
                                {(reportLoading || viewDataLoading) && <div className="loader_box ms-auto"><div className="loader_14"></div></div>}
                            </div>

                            <div className="row">
                                {!viewDataLoading && (documentsList.data.length > 0 ? (
                                    documentsList.data.map((doc) => (
                                        <div className="col-md-6 col-xl-4 project-card" key={doc.id}>
                                            <div className="card hover-effect">
                                                <div className="card-header">
                                                    <div className="d-flex align-items-center">
                                                         <h6 className="m-0 text-dark f-w-600 flex-grow-1 ps-2">{doc.title}</h6>
                                                        <div className="dropdown">
                                                            <button aria-expanded="false" className="bg-none border-0" data-bs-toggle="dropdown" type="button">
                                                                <i className="ti ti-dots-vertical text-dark" />
                                                            </button>
                                                            <ul className="dropdown-menu dropdown-menu-end">
                                                                <li>
                                                                    <a className="dropdown-item" onClick={() => navigate(`/docs/documents/edit/${doc.id}`)}>
                                                                        <i className="ti ti-edit text-success" /> Edit
                                                                    </a>
                                                                </li>
                                                                <li>
                                                                    <a className="dropdown-item delete-button" onClick={() => handleDeleteClick(doc)}>
                                                                        <i className="ti ti-trash text-danger" /> Delete
                                                                    </a>
                                                                </li>
                                                            </ul>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="card-body">
                                                    <span className={`badge bg-${doc.status === 'published' ? 'success' : 'warning'}`}>{doc.status}</span>
                                                    <p className="f-s-14 mt-2">Parent: {doc.parent ? doc.parent.title : 'None'}</p>
                                                    <p className="f-s-14">Author: {doc.author ? doc.author.name : 'N/A'}</p>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="col-12">
                                        <div className="card">
                                            <div className="card-body text-center">
                                                <p>No Documents Found</p>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </>
    );
};

export default DocumentsPage;

